import { memo, useCallback } from "react";
import { Link, useNavigate } from "react-router";

import { getImageUrl } from "@/lib/aws-amplify/host-storage";
import { getHotelTopAboutUrl, getPlanDetailUrl, getRoomDetailUrl } from "@/lib/chillnn/get-detail-url";
import type { S3Object } from "@/lib/gql/__generated__/graphql";

import { getMagazineEditPageUrl } from "@/components/features/design/magazine/edit/_shared/utils/get-magazine-edit-page-url";

import { Button } from "@/components/ui/button/button";
import EditDocument from "@/components/ui/icon/edit-document";
import OpenInNew from "@/components/ui/icon/open-in-new";
import { Image } from "@/components/ui/image/image";

type Props = {
	hotelID: string;

	id: string;
	name: string;
	mainKeyVisual: S3Object;

	searchParams:
		| {
				resourceType: "Plan" | "Room";
				resourceID: string;
		  }
		| {
				resourceType: "AboutHotel";
				resourceID: undefined;
		  };
};

export const DesignMagazinePlanListItem = memo(({ hotelID, name, mainKeyVisual, searchParams }: Props) => {
	const { resourceID, resourceType } = searchParams;

	const mainKVUrl = getImageUrl(mainKeyVisual);

	const editPagePath =
		resourceType === "AboutHotel"
			? getMagazineEditPageUrl({
					hotelID,
					magazineResourceID: undefined,
					magazineResourceType: resourceType,
				})
			: getMagazineEditPageUrl({
					hotelID,
					magazineResourceID: resourceID,
					magazineResourceType: resourceType,
				});

	const magazinePagePath =
		resourceType === "AboutHotel"
			? getHotelTopAboutUrl(hotelID)
			: resourceType === "Plan"
				? getPlanDetailUrl(hotelID, resourceID)
				: getRoomDetailUrl(hotelID, resourceID);

	const navigate = useNavigate();
	const handleClick = useCallback(() => {
		navigate(editPagePath);
	}, [editPagePath, navigate]);

	return (
		<div onClick={handleClick} className="group flex items-center justify-between gap-x-6 hover:cursor-pointer">
			<div className="flex items-center gap-x-6">
				<Image src={mainKVUrl} className="h-16 w-16 rounded object-cover" />
				<p className="group-hover:underline">{name}</p>
			</div>
			<div className="flex items-center gap-x-6">
				<Link to={editPagePath} onClick={(e) => e.stopPropagation()} viewTransition>
					<Button type="button" variant="input-ish" size="icon" tooltip="記事編集">
						<EditDocument size="sm" />
					</Button>
				</Link>

				<Link to={magazinePagePath} target="_blank" onClick={(e) => e.stopPropagation()} viewTransition>
					<Button type="button" variant="input-ish" size="icon" tooltip="表示を確認">
						<OpenInNew size="sm" />
					</Button>
				</Link>
			</div>
		</div>
	);
});

DesignMagazinePlanListItem.displayName = "DesignMagazinePlanListItem";
